import { useState, useRef } from "react";
import { CONFIG, BANKS, MONTHS } from "./config";
import "./App.css";

const plnFmt = new Intl.NumberFormat("pl-PL", {
  style: "currency",
  currency: "PLN",
  maximumFractionDigits: 0,
});
const pln2 = new Intl.NumberFormat("pl-PL", {
  style: "currency",
  currency: "PLN",
  maximumFractionDigits: 2,
});
const num = (v) => parseFloat(String(v).replace(/\s/g, "").replace(",", "."));

/* ---------- ikony ---------- */
const Check = (p) => (
  <svg
    width={p.s || 18}
    height={p.s || 18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const Arrow = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const Back = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
);

const EMPTY = {
  year: "",
  month: "",
  currency: "CHF",
  amount: "",
  termYears: "", // wartość z inputu (lata) — tylko UI
  term: "", // wartość kanoniczna w miesiącach — wysyłana do API
  margin: "",
  repaid: "",
  name: "",
  email: "",
  phone: "",
  consent: false,
};

export default function App() {
  const [step, setStep] = useState(1);
  const [bank, setBank] = useState(null);
  const [amtMode, setAmtMode] = useState("PLN"); // PLN | FCY
  const [f, setF] = useState(EMPTY);
  const [errs, setErrs] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errBanner, setErrBanner] = useState("");
  const topRef = useRef(null);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // input w latach, ale w stanie trzymamy miesiące
  const updTerm = (v) => {
    const years = num(v);
    setF((s) => ({
      ...s,
      termYears: v,
      term: years > 0 ? Math.round(years * 12) : "",
    }));
  };

  const years = (() => {
    const y = new Date().getFullYear();
    return Array.from({ length: y - 1999 }, (_, i) => y - i);
  })();

  const goStep = (n) => {
    setStep(n);
    setTimeout(() => topRef.current?.scrollIntoView({ block: "start" }), 0);
  };

  const next1 = () => {
    if (!bank) {
      setErrs((e) => ({ ...e, bank: true }));
      return;
    }
    goStep(2);
  };

  function validateStep2() {
    const e = {};
    if (!f.year) e.year = true;
    if (!f.month) e.month = true;
    if (!(num(f.amount) > 0)) e.amount = true;
    const t = num(f.termYears);
    if (!(t > 0 && t <= 50)) e.term = true;
    if (f.name.trim().length < 3) e.name = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = true;
    if (f.phone.replace(/[^\d]/g, "").length < 9) e.phone = true;
    if (!f.consent) e.consent = true;
    setErrs(e);
    return Object.keys(e).length === 0;
  }

  function buildCalcPayload() {
    const amount = num(f.amount);
    const startDate = `${f.year}-${String(f.month).padStart(2, "0")}-01`;
    const marginRaw = num(f.margin);
    const p = {
      currency: f.currency,
      startDate,
      loanTermMonths: Math.round(num(f.term)), // f.term już w miesiącach
      bankName: bank,
      installmentType: "równe",
      margin: marginRaw > 0 ? marginRaw : 1.25, // server dzieli przez 100 gdy >1
    };
    if (amtMode === "PLN") p.loanAmountPLN = amount;
    else p.loanAmount = amount;
    if (f.repaid) p.repaidDate = f.repaid;
    return p;
  }

  // notatka dołączana do leada w CRM
  function buildLeadNote(res) {
    const parts = [
      "Lead z kalkulatora CHF (mojefranki.pl).",
      `Uruchomienie: ${f.year}-${String(f.month).padStart(2, "0")}.`,
      `Okres: ${f.termYears} lat.`,
      `Marża: ${f.margin || "1.25"}%.`,
    ];
    if (res) {
      parts.push(
        `Łączna korzyść: ${plnFmt.format(res.lacznaKorzysc || 0)}.`,
        `Nadpłata: ${plnFmt.format(res.nadplata || 0)}.`,
        `Spadek salda: ${plnFmt.format(res.zmniejszenieSalda || 0)}.`,
        `Saldo wg banku: ${plnFmt.format(res.currentBalancePLN || 0)}.`,
        `Rat zapłaconych: ${res.paidInstallments ?? "—"}.`,
        res.uuid ? `UUID analizy: ${res.uuid}.` : ""
      );
    } else {
      parts.push(
        "Analiza nie policzyła się — kontakt zachowany do oddzwonienia."
      );
    }
    return parts.filter(Boolean).join(" ");
  }

  // body zgodne z api.kppp.pl/addLeadWeb
  function buildLead(res) {
    return {
      name: f.name.trim(),
      phone: f.phone.trim(),
      mail: f.email.trim(),
      source: "MF",
      bank: bank,
      agreement: "",
      creditAmmount: res?.loanAmountPLN ?? num(f.amount),
      creditCurrency: f.currency, // CHF / EUR / USD
      creditDate: `${f.year}-${String(f.month).padStart(2, "0")}-01`,
      addUser: "Admin MF",
      type: "CHF",
      end: f.repaid || "",
      length: Math.round(num(f.termYears)) || "", // okres w miesiącach (creditLength)
      note: buildLeadNote(res),
      files: res?.excelUrl || "",
    };
  }

  async function callFx(payload) {
    const res = await fetch(CONFIG.API_BASE + CONFIG.FX_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": CONFIG.API_KEY,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Błąd serwera (${res.status}).`);
    return data;
  }

  // rejestracja konwersji Google Ads (odpalana po dodaniu leada do CRM)
  function trackConversion() {
    if (typeof window === "undefined" || typeof window.gtag !== "function")
      return;
    window.gtag("event", "conversion", {
      send_to: "AW-11395538289/34XGCILnv6AcEPG66Lkq",
      event_callback: () => {}, // brak nawigacji do wstrzymania — no-op
    });
  }

  async function sendLead(lead) {
    if (!CONFIG.SEND_LEAD) return;
    try {
      const res = await fetch(CONFIG.API_BASE + "/api/leads/kppp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": CONFIG.API_KEY,
        },
        body: JSON.stringify(lead),
      });
      if (res.ok) trackConversion(); // konwersja dopiero po udanym zapisie w CRM
    } catch (_) {
      /* nie blokuj wyniku, gdy CRM chwilowo niedostępny */
    }
  }

  async function onCalc() {
    if (!validateStep2()) {
      setTimeout(
        () =>
          document.querySelector(".bad")?.scrollIntoView({ block: "center" }),
        0
      );
      return;
    }
    setLoading(true);
    setErrBanner("");
    const params = buildCalcPayload();
    try {
      const res = await callFx(params);
      sendLead(buildLead(res));
      setResult(res);
      goStep(3);
    } catch (err) {
      sendLead(buildLead(null)); // zachowaj kontakt mimo błędu liczenia
      setResult(null);
      setErrBanner(
        "Nie udało się policzyć analizy: " +
          err.message +
          " Sprawdź dane lub spróbuj ponownie — doradca i tak otrzyma Twoje zgłoszenie."
      );
      goStep(3);
    } finally {
      setLoading(false);
    }
  }

  const reset = () => {
    setStep(1);
    setBank(null);
    setAmtMode("PLN");
    setF(EMPTY);
    setErrs({});
    setResult(null);
    setErrBanner("");
    setTimeout(() => topRef.current?.scrollIntoView({ block: "start" }), 0);
  };

  const pillCls = (s) =>
    `step-pill${step === s ? " active" : ""}${s < step ? " done" : ""}`;

  return (
    <>
      <div className="analyzer" id="analiza" ref={topRef}>
        <div className="panel">
          <div className="panel-head">
            <div>
              <h2>Analiza zapisów Twojej umowy</h2>
              <div className="sub">Krok {step} z 3</div>
            </div>
            <div className="steps">
              <div className={pillCls(1)}>
                <span className="n">1</span>Bank
              </div>
              <div className={pillCls(2)}>
                <span className="n">2</span>Dane kredytu
              </div>
              <div className={pillCls(3)}>
                <span className="n">3</span>Wynik
              </div>
            </div>
          </div>

          <div className="panel-body">
            {step === 1 && (
              <div className="step">
                <div className="bank-grid">
                  {BANKS.map(([name, succ], i) => (
                    <button
                      key={i}
                      type="button"
                      className={`bank${bank === name ? " sel" : ""}`}
                      onClick={() => {
                        setBank(name);
                        setErrs((e) => ({ ...e, bank: false }));
                      }}
                    >
                      <b>{name}</b>
                      {succ && <small>{succ}</small>}
                    </button>
                  ))}
                </div>
                {errs.bank && (
                  <div className="err">Wybierz bank, aby przejść dalej.</div>
                )}
                <div className="nav-row">
                  <span />
                  <button className="btn btn-primary" onClick={next1}>
                    Dalej
                    <Arrow />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="step">
                <div className="sec-title">Parametry kredytu</div>
                <div className="grid" style={{ marginBottom: 6 }}>
                  <div>
                    <label className="lab" htmlFor="year">
                      Rok zaciągnięcia kredytu <span className="req">*</span>
                    </label>
                    <select
                      id="year"
                      className={errs.year ? "bad" : ""}
                      value={f.year}
                      onChange={(e) => upd("year", e.target.value)}
                    >
                      <option value="">— wybierz —</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                    {errs.year && <div className="err">Wybierz rok.</div>}
                  </div>
                  <div>
                    <label className="lab" htmlFor="month">
                      Miesiąc zaciągnięcia <span className="req">*</span>
                    </label>
                    <select
                      id="month"
                      className={errs.month ? "bad" : ""}
                      value={f.month}
                      onChange={(e) => upd("month", e.target.value)}
                    >
                      <option value="">— wybierz —</option>
                      {MONTHS.map((m, i) => (
                        <option key={i} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </select>
                    {errs.month && <div className="err">Wybierz miesiąc.</div>}
                  </div>

                  <div>
                    <label className="lab" htmlFor="currency">
                      Waluta kredytu <span className="req">*</span>
                    </label>
                    <select
                      id="currency"
                      value={f.currency}
                      onChange={(e) => upd("currency", e.target.value)}
                    >
                      <option value="CHF">CHF — frank szwajcarski</option>
                      <option value="EUR">EUR — euro</option>
                      <option value="USD">USD — dolar amerykański</option>
                    </select>
                  </div>
                  <div>
                    <label className="lab">
                      Kwota podana w <span className="req">*</span>
                    </label>
                    <div
                      className="seg"
                      role="group"
                      aria-label="Jednostka kwoty"
                    >
                      <button
                        type="button"
                        className={amtMode === "PLN" ? "on" : ""}
                        onClick={() => setAmtMode("PLN")}
                      >
                        złotych (PLN)
                      </button>
                      <button
                        type="button"
                        className={amtMode === "FCY" ? "on" : ""}
                        onClick={() => setAmtMode("FCY")}
                      >
                        walucie
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="lab" htmlFor="amount">
                      Kwota kredytu z umowy <span className="req">*</span>
                      <span className="hint">
                        {amtMode === "PLN" ? "w PLN" : "w walucie kredytu"}
                      </span>
                    </label>
                    <input
                      id="amount"
                      inputMode="decimal"
                      type="number"
                      className={errs.amount ? "bad" : ""}
                      placeholder="np. 350000"
                      value={f.amount}
                      onChange={(e) => upd("amount", e.target.value)}
                    />
                    {errs.amount && (
                      <div className="err">Podaj kwotę kredytu.</div>
                    )}
                  </div>
                  <div>
                    <label className="lab" htmlFor="term">
                      Okres kredytowania <span className="req">*</span>
                      <span className="hint">w latach</span>
                    </label>
                    <input
                      id="term"
                      inputMode="numeric"
                      className={errs.term ? "bad" : ""}
                      placeholder="np. 30"
                      value={f.termYears}
                      onChange={(e) => updTerm(e.target.value)}
                    />
                    {errs.term && (
                      <div className="err">Podaj okres w latach (1–50).</div>
                    )}
                  </div>

                  <div>
                    <label className="lab" htmlFor="margin">
                      Marża banku <span className="hint">% — opcjonalnie</span>
                    </label>
                    <input
                      id="margin"
                      inputMode="decimal"
                      placeholder="np. 1.25 (domyślnie 1.25%)"
                      value={f.margin}
                      onChange={(e) => upd("margin", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="lab" htmlFor="repaid">
                      Data całkowitej spłaty{" "}
                      <span className="hint">jeśli spłacony</span>
                    </label>
                    <input
                      id="repaid"
                      type="date"
                      value={f.repaid}
                      onChange={(e) => upd("repaid", e.target.value)}
                    />
                  </div>
                </div>

                <div className="divider" />

                <div className="sec-title" style={{ marginBottom: "2vh" }}>
                  Dane kontaktowe
                </div>

                <div className="grid">
                  <div className="full">
                    <label className="lab" htmlFor="name">
                      Imię i nazwisko <span className="req">*</span>
                    </label>
                    <input
                      id="name"
                      autoComplete="name"
                      className={errs.name ? "bad" : ""}
                      placeholder="Jan Kowalski"
                      value={f.name}
                      onChange={(e) => upd("name", e.target.value)}
                    />
                    {errs.name && (
                      <div className="err">Podaj imię i nazwisko.</div>
                    )}
                  </div>
                  <div>
                    <label className="lab" htmlFor="email">
                      Adres e-mail <span className="req">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className={errs.email ? "bad" : ""}
                      placeholder="jan.kowalski@email.pl"
                      value={f.email}
                      onChange={(e) => upd("email", e.target.value)}
                    />
                    {errs.email && (
                      <div className="err">Podaj poprawny adres e-mail.</div>
                    )}
                  </div>
                  <div>
                    <label className="lab" htmlFor="phone">
                      Numer telefonu <span className="req">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      className={errs.phone ? "bad" : ""}
                      placeholder="600 700 800"
                      value={f.phone}
                      onChange={(e) => upd("phone", e.target.value)}
                    />
                    {errs.phone && (
                      <div className="err">Podaj poprawny numer telefonu.</div>
                    )}
                  </div>
                  <div className="full">
                    <div className="consent">
                      <input
                        id="consent"
                        type="checkbox"
                        checked={f.consent}
                        onChange={(e) => upd("consent", e.target.checked)}
                      />
                      <label htmlFor="consent">
                        Zapoznałem/-am się z{" "}
                        <a
                          href="https://mojefranki.pl/polityka-prywatnosci/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          polityką prywatności
                        </a>{" "}
                        i akceptuję jej postanowienia. Wyrażam zgodę na
                        przetwarzanie danych w celu kontaktu (art. 6 ust. 1 lit.
                        a RODO). <span className="req">*</span>
                      </label>
                    </div>
                    {errs.consent && (
                      <div className="err">
                        Zgoda jest wymagana, aby przesłać zgłoszenie.
                      </div>
                    )}
                  </div>
                </div>

                <div className="nav-row">
                  <button className="btn btn-ghost" onClick={() => goStep(1)}>
                    <Back />
                    Wstecz
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={onCalc}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spin" /> Liczę…
                      </>
                    ) : (
                      <>
                        Oblicz korzyść
                        <Arrow />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="step">
                {errBanner && <div className="banner error">{errBanner}</div>}
                {result && (
                  <Result r={result} f={f} bank={bank} onReset={reset} />
                )}
                {!result && !errBanner && (
                  <p className="center">Brak wyniku.</p>
                )}
                {!result && errBanner && (
                  <div className="result-cta">
                    <button
                      className="btn btn-primary"
                      onClick={() => goStep(2)}
                    >
                      Wróć do formularza
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------- panel wyniku ---------- */
function Row({ title, sub, val, cls = "" }) {
  const v = typeof val === "number" && isFinite(val) ? pln2.format(val) : "—";
  return (
    <div className={`lrow ${cls}`}>
      <div className="k">
        {title}
        <small>{sub}</small>
      </div>
      <div className="v num">{v}</div>
    </div>
  );
}

function Result({ r, f, bank, onReset }) {
  const benefit = r.lacznaKorzysc ?? 0;
  const positive = benefit > 0;
  const firstName = f.name.trim().split(" ")[0] || "";
  const startInfo = `${MONTHS[parseInt(f.month, 10) - 1] || ""} ${f.year}`;

  return (
    <div className="step">
      <div className="ledger-top">
        <div className="cap">Szacowana łączna korzyść</div>
        <div className="big num">{plnFmt.format(Math.max(0, benefit))}</div>
        <div className="note">
          {positive
            ? "Suma nadpłaty ponad wypłacony kapitał oraz spadku salda zadłużenia po unieważnieniu umowy."
            : "Na podstawie podanych danych nadpłata nie powstała jeszcze w pełni — szczegóły omówi doradca."}
        </div>
      </div>

      <div className="ledger-rows">
        <Row
          title="Nadpłata ponad kapitał"
          sub="ile zapłacono ponad wypłaconą kwotę"
          val={r.nadplata}
          cls="gain"
        />
        <Row
          title="Spadek salda zadłużenia"
          sub="o ile maleje saldo wg unieważnienia"
          val={r.zmniejszenieSalda}
          cls="gain"
        />
        <Row
          title="Obecne saldo wg banku"
          sub="ile bank wykazuje dziś do spłaty"
          val={r.currentBalancePLN}
          cls="warn"
        />
        <Row
          title="Saldo po unieważnieniu"
          sub="ile kapitału pozostałoby do zwrotu"
          val={r.saldoPoUniewaznieniu}
        />
        <Row
          title="Suma zapłacona bankowi"
          sub="wszystkie raty do dziś"
          val={r.sumaZaplaconaBanku}
        />
        <div className="lrow total">
          <div className="k">Łączna korzyść (szacunek)</div>
          <div className="v num">{plnFmt.format(Math.max(0, benefit))}</div>
        </div>
      </div>

      <div className="meta-strip">
        <span className="chip">
          <b>Bank:</b> {r.bankName || bank || "—"}
        </span>
        <span className="chip">
          <b>Waluta:</b> {r.currency}
        </span>
        <span className="chip">
          <b>Uruchomienie:</b> {startInfo}
        </span>
        <span className="chip">
          <b>Kwota kredytu:</b> {pln2.format(r.loanAmountPLN || 0)}
        </span>
        {r.startRate != null && (
          <span className="chip">
            <b>Kurs startowy:</b> {Number(r.startRate).toFixed(4)}
          </span>
        )}
        <span className="chip">
          <b>Rat zapłaconych:</b> {r.paidInstallments}
        </span>
      </div>

      <p className="disclaim">
        Wynik ma charakter wyłącznie szacunkowy, oparty o dane wprowadzone w
        formularzu oraz kursy NBP i stawki referencyjne z bazy. Nie stanowi
        porady prawnej ani gwarancji rozstrzygnięcia. Ostateczna ocena wymaga
        analizy pełnej dokumentacji kredytowej.
      </p>

      <div className="result-cta">
        <button className="btn btn-primary" onClick={onReset}>
          Zresetuj wyniki
        </button>
      </div>
    </div>
  );
}
